import { LinkedAsset } from '@/types/AssetFolders';
import {
    DataModel,
    DataModelAttributeOption,
    DataModelCollectionFilteringFunction,
    DataModelCustomFilter,
    DataModelCustomLayoutFilter,
    DataModelGroupTitleFilter,
    DataModelItemTitleFilter,
    DataModelSortingFunction,
    DataModelUiReferenceFilter
} from '@/types/DataModel';

//eslint-disable-next-line
declare const angular: any;

//eslint-disable-next-line
export const getRootScope = (injector: any) => injector.get('$rootScope');

export const FILTERS = {
    MEDIA_ASSET_LAYOUT_TITLE: 'mediaAssetLayoutTitleFilterFilter'
};

export const getFilter = (filterName: string) => {
    const documentBody = document.getElementsByTagName('body');
    const angularJsInjector = angular.element(documentBody).injector();

    if (!angularJsInjector) {
        return undefined;
    }

    // AngularJS registers a filter named "foo" as the provider "fooFilter".
    // Missing data-model filters are ignored, matching $rootScope.getFilter.
    // Storybook's injector only implements get, so skip the has() check there.
    const providerName = `${filterName}Filter`;
    if (typeof angularJsInjector.has === 'function' && !angularJsInjector.has(providerName)) {
        return undefined;
    }

    //eslint-disable-next-line
    return angularJsInjector.get(providerName);
};

export const getCustomFilter = (filterName: string) : DataModelCustomFilter | undefined => {
    return getFilter(filterName);
};

export const getCustomLayoutFilter = (filterName: string) : DataModelCustomLayoutFilter | undefined => {
    return getFilter(filterName);
};

export const getCustomCollectionFilter = <T>(filterName: string): DataModelCollectionFilteringFunction<T> | undefined => {
    return getFilter(filterName);
};

export const getCustomUiReferenceFilter = (filterName: string) : DataModelUiReferenceFilter | undefined => {
    return getFilter(filterName);
};

type GetSortingFunctionType = <Row, Value>(sortingFunctionName: string) => DataModelSortingFunction<Row, Value> | undefined;
export const getSortingFunction: GetSortingFunctionType  = (sortingFunctionName: string) => {
    const documentBody = document.getElementsByTagName('body');
    const angularJsInjector = angular.element(documentBody).injector();

    //eslint-disable-next-line
    return angularJsInjector.get(sortingFunctionName);
};

export const getItemTitleFilter = (filterName: string) : DataModelItemTitleFilter | undefined => {
    return getFilter(filterName);
};

export const getGroupTitleFilter = (filterName: string) : DataModelGroupTitleFilter | undefined => {
    return getFilter(filterName);
};

export const getSecondaryItemTitleFilter = (filterName: string) : DataModelItemTitleFilter | undefined => {
    return getFilter(filterName);
};

export const getDataModel = () => {
    const documentBody = document.getElementsByTagName('body');
    const angularJsInjector = angular.element(documentBody).injector();
    const rootScope = getRootScope(angularJsInjector);

    return rootScope.dataModel as DataModel;
};

/**
 * After React loads service-backed option list data, mirror it onto the shared AngularJS DataModel so
 * legacy paths (e.g. EnumAttributeService / optionListOptionsAsync) do not fire a duplicate GET .../options?serviceName=...
 *
 * Returns the mapped, normalized, and translated copy that was assigned to the
 * AngularJS DataModel. Callers should prefer this return value over their input
 * `options` array when populating React state, because `translateAllOptions`
 * runs in-place on the returned array (so `option.translatedOption` is set
 * here but not on the caller's original input).
 *
 * DRIFT-PARTNER — the field whitelist below
 * (`name, serviceName, key, value, label, labels, icon, icons, groups`)
 * must stay identical to the AngularJS server-fetch normalization at
 * `web/main/src/main/webapp/app/dataModel.js#fetchAndCacheOptionListAsync`
 * (~lines 1558-1570). Both produce the AngularJS-side option shape, just
 * from different inputs (raw server response there, already-normalized React
 * options here). Adding a new field on either side without the other will
 * cause AngularJS consumers (legacy editor + custom data-model filters) to
 * see different shapes depending on whether the data was loaded by AngularJS
 * or by React. See also the React-side normalizer
 * `components/datamodel/optionListUtils.ts#normalizeOptionListOption`.
 */
export const hydrateAngularOptionListFromOptions = (
    dataModel: DataModel,
    optionListName: string,
    options: DataModelAttributeOption[]
): DataModelAttributeOption[] => {
    if (!dataModel?.optionList) {
        return options;
    }
    const optionList = dataModel.optionList(optionListName);
    if (!optionList || typeof optionList !== 'object') {
        return options;
    }

    const mapped = options.map((option) => ({
        name: option.name ?? option.key,
        serviceName: option.serviceName || '',
        key: option.key ?? option.name,
        value: option.label ?? option.value,
        label: option.label ?? option.value,
        labels: option.labels,
        icon: option.icon,
        icons: option.icons,
        groups: option.groups,
    })) as DataModelAttributeOption[];

    optionList.options = mapped;
    optionList.optionsLoaded = true;
    delete optionList.optionsLoadingPromise;

    try {
        const injector = getInjector();
        const $rootScope = getRootScope(injector);
        if (typeof $rootScope.translateAllOptions === 'function') {
            $rootScope.translateAllOptions(null, optionList.options);
        }
    } catch {
        // No Angular injector (e.g. Storybook without shell)
    }

    return mapped;
};

export const getInjector = () => {
    const documentBody = document.getElementsByTagName('body');
    const angularJsInjector = angular.element(documentBody).injector();
    return angularJsInjector;
};

/**
 * Replaces the query string without adding a history entry. Goes through
 * AngularJS `$location` when the shell is present, because its location watcher
 * would otherwise revert URL changes made behind its back.
 */
export const replaceLocationSearch = (params: Record<string, string | string[]>, fallbackSearch: string) => {
    const injector = typeof angular === 'undefined' ? undefined : getInjector();
    if (!injector) {
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${fallbackSearch}${window.location.hash}`);
        return;
    }
    const $location = injector.get('$location');
    getRootScope(injector).$evalAsync(() => {
        $location.search(params).replace();
    });
};

//eslint-disable-next-line
const applyLocationUrl = (injector: any, url: string) => {
    const $rootScope = getRootScope(injector);
    const $location = injector.get('$location');
    // `$evalAsync` alone does not always start a digest here, so the change would wait for the next one.
    const move = () => $location.url(url);
    if ($rootScope.$$phase) {
        $rootScope.$evalAsync(move);
    } else {
        $rootScope.$apply(move);
    }
};

/**
 * Moves to `url` (path and query string) through AngularJS `$location`: the shell stays
 * loaded and only the routed page is replaced. Without the shell (Storybook, unit tests)
 * it falls back to a full page load.
 */
export const navigateTo = (url: string) => {
    const injector = typeof angular === 'undefined' ? undefined : getInjector();
    if (!injector) {
        window.location.assign(url);
        return;
    }
    applyLocationUrl(injector, url);
};

/**
 * Keeps the routed page mounted while the location moves between paths that `isOwnPath`
 * accepts, e.g. a list and its detail view: like `$location.displayUrl`, the route is put
 * back after `$locationChangeSuccess`, for own navigation and for back and forward alike.
 * `onChange` gets the pathname after every location change. Returns the unsubscribe
 * function, or `undefined` when the AngularJS shell is absent.
 */
export const holdAngularRoute = (isOwnPath: (pathname: string) => boolean, onChange: (pathname: string) => void): (() => void) | undefined => {
    const injector = typeof angular === 'undefined' ? undefined : getInjector();
    if (!injector) {
        return undefined;
    }
    const $rootScope = getRootScope(injector);
    const $route = injector.get('$route');
    const pathnameOf = (url: string) => new URL(url, window.location.href).pathname;
    let held: { url: string; route: unknown } | undefined;
    const offStart = $rootScope.$on('$locationChangeStart', (_event: unknown, newUrl: string, oldUrl: string) => {
        held = isOwnPath(pathnameOf(newUrl)) && isOwnPath(pathnameOf(oldUrl)) ? { url: newUrl, route: $route.current } : undefined;
    });
    // Runs after the listener of $route, which already switched `$route.current`; ngRoute only
    // mounts the new page when the route is still current once it is resolved.
    const offSuccess = $rootScope.$on('$locationChangeSuccess', (_event: unknown, newUrl: string) => {
        if (held && held.url === newUrl) {
            $route.current = held.route;
        }
        held = undefined;
        onChange(pathnameOf(newUrl));
    });
    return () => {
        offStart();
        offSuccess();
    };
};

interface AngularLocationChange {
    newUrl: string;
    oldUrl: string;
    /** Keeps the current location. */
    prevent: () => void;
    /** Moves to `newUrl` after `prevent`, e.g. once the user confirmed. */
    proceed: () => void;
}

/**
 * Calls `listener` before AngularJS changes the location: links of the shell, `$location`
 * changes and the browser's back and forward buttons. Returns the unsubscribe function, or
 * `undefined` when the AngularJS shell is absent (Storybook, unit tests).
 */
export const onAngularLocationChangeStart = (listener: (change: AngularLocationChange) => void): (() => void) | undefined => {
    const injector = typeof angular === 'undefined' ? undefined : getInjector();
    if (!injector) {
        return undefined;
    }
    const $rootScope = getRootScope(injector);
    const $location = injector.get('$location');
    return $rootScope.$on('$locationChangeStart', (event: { preventDefault: () => void }, newUrl: string, oldUrl: string) => {
        listener({
            newUrl,
            oldUrl,
            prevent: () => event.preventDefault(),
            proceed: () => {
                const absUrl: string = $location.absUrl();
                const base = absUrl.slice(0, absUrl.length - $location.url().length);
                if (newUrl.startsWith(base)) {
                    applyLocationUrl(injector, newUrl.slice(base.length));
                } else {
                    window.location.assign(newUrl);
                }
            },
        });
    });
};

/**
 * Mirror `$rootScope.hasSettingFeature` from application-controller.js.
 * Returns false when the Angular shell is absent (Storybook, unit tests).
 */
export const hasSettingFeature = (feature: string): boolean => {
    try {
        const $rootScope = getRootScope(getInjector());
        if (typeof $rootScope.hasSettingFeature === 'function') {
            return $rootScope.hasSettingFeature(feature);
        }
    } catch {
        // No Angular injector
    }
    return false;
};

export const getCompiler = () => {
    const documentBody = document.getElementsByTagName('body');
    const angularJsInjector = angular.element(documentBody).injector();
    return angularJsInjector.get('$compile');
};

export const showAssetFoldersModal = (onFileSelected: (linkedAsset: LinkedAsset, linkedAssetPath: string) => void, acceptedTypes: string) => {
    const angularJsInjector = getInjector();
    //eslint-disable-next-line
    const $reactBridge = angularJsInjector.get('ReactBridge');
    $reactBridge.showAssetFoldersModal(onFileSelected, acceptedTypes);
};
